import { Button, Group, SimpleGrid, Stack, Table } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { InfoIcon, Modal, Text } from "@/components/ui/elements";

import type { MyPageQuery } from "@/gql/__generated__/graphql";

/** サーバーが集計したキャンセル率・件数（期間ごと）。取得に失敗した期間は null */
export type CancellationStats = NonNullable<
  NonNullable<MyPageQuery["usageStatus"]>["cancellationStatsAllPeriod"]
>;

type Period = {
  label: string;
  description: string;
  stats: CancellationStats | null;
};

type Props = {
  opened: boolean;
  close: () => void;
  lastThreeMonths: CancellationStats | null;
  allPeriod: CancellationStats | null;
};

const NOTE =
  "直近3か月、全期間のキャンセル率、直前キャンセル率によって応募できる件数が制限されることがあります。";

/** 判定対象件数が0の期間は率を出さない（0% と表示すると実績が良いように見えるため） */
function formatRate(rate: number, resolvedCount: number) {
  return resolvedCount === 0 ? "—" : `${rate}%`;
}

function fetchErrorMessage(label: string) {
  return `${label}の値を読み込めませんでした。時間をおいて、マイページを開き直してください。`;
}

function noRecordMessage(label: string) {
  return `${label}は勤務・キャンセルがないため、率は「—」と表示しています。`;
}

export function CancellationStatsModal({
  opened,
  close,
  lastThreeMonths,
  allPeriod,
}: Props) {
  const isMobile = useMediaQuery("(max-width: 50em)");
  const periods: Period[] = [
    {
      label: "直近3か月",
      description: "直近3か月に決まったお仕事",
      stats: lastThreeMonths,
    },
    {
      label: "全期間",
      description: "これまでのすべてのお仕事",
      stats: allPeriod,
    },
  ];

  return (
    <Modal
      opened={opened}
      close={close}
      title="キャンセル率・件数の詳細"
      fullScreen={isMobile}
      size="xl"
    >
      <Stack gap="md">
        <Group
          gap="xs"
          align="flex-start"
          wrap="nowrap"
          p="xs"
          style={{
            borderRadius: "var(--mantine-radius-md)",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          <InfoIcon
            size={16}
            aria-hidden="true"
            style={{
              color: "var(--mantine-color-gray-7)",
              flexShrink: 0,
              marginTop: 2,
            }}
          />
          <Text fz="xs" c="dark.6">
            {NOTE}
          </Text>
        </Group>

        {isMobile ? (
          periods.map((period) => (
            <PeriodBlock key={period.label} period={period} />
          ))
        ) : (
          <PeriodTable periods={periods} />
        )}

        <Explanation />

        <Group justify={isMobile ? "stretch" : "flex-end"}>
          <Button
            variant="default"
            radius="md"
            onClick={close}
            fullWidth={isMobile}
            mih={44}
          >
            閉じる
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** スマホ幅：期間ごとのブロック */
function PeriodBlock({ period }: { period: Period }) {
  const { label, description, stats } = period;

  return (
    <section
      aria-label={label}
      style={{
        border: "1px solid var(--mantine-color-gray-3)",
        borderRadius: "var(--mantine-radius-md)",
        overflow: "hidden",
      }}
    >
      <Group
        gap="xs"
        align="baseline"
        px="sm"
        py={8}
        style={{
          backgroundColor: "var(--mantine-color-gray-0)",
          borderBottom: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        <Text fz="sm" fw={700}>
          {label}
        </Text>
        <Text fz="xs" c="dark.5">
          {description}
        </Text>
      </Group>

      {stats == null ? (
        <FetchError label={label} />
      ) : (
        <Stack gap="xs" p="sm">
          <SimpleGrid cols={2} spacing="xs">
            <RateTile
              name="キャンセル率"
              value={formatRate(stats.cancellationRate, stats.resolvedCount)}
            />
            <RateTile
              name="直前キャンセル率"
              value={formatRate(
                stats.lateCancellationRate,
                stats.resolvedCount,
              )}
            />
          </SimpleGrid>

          <Stack gap={0}>
            <CountRow name="勤務数" value={`${stats.workedCount}回`} />
            <CountRow
              name="キャンセル件数"
              value={`${stats.cancellationCount}回`}
            />
            <CountRow
              name="うち直前キャンセル件数"
              value={`${stats.lateCancellationCount}回`}
              indent
            />
            <CountRow
              name="判定対象件数（勤務数＋キャンセル件数）"
              value={`${stats.resolvedCount}件`}
              total
            />
          </Stack>

          {stats.resolvedCount === 0 ? (
            <Text fz="xs" c="dark.5">
              {noRecordMessage(label)}
            </Text>
          ) : null}
        </Stack>
      )}
    </section>
  );
}

function RateTile({ name, value }: { name: string; value: string }) {
  return (
    <Stack
      gap={2}
      px="xs"
      py={8}
      style={{
        border: "1px solid var(--mantine-color-gray-2)",
        borderRadius: "var(--mantine-radius-md)",
      }}
    >
      <Text fz="xs" c="dark.5">
        {name}
      </Text>
      <Text fz={22} fw={700} lh={1.3}>
        {value}
      </Text>
    </Stack>
  );
}

function CountRow({
  name,
  value,
  indent = false,
  total = false,
}: {
  name: string;
  value: string;
  indent?: boolean;
  total?: boolean;
}) {
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      py={4}
      pl={indent ? "sm" : 0}
      mt={total ? 4 : 0}
      style={
        total
          ? { borderTop: "1px dashed var(--mantine-color-gray-4)" }
          : undefined
      }
    >
      <Text fz="sm" c="dark.5">
        {name}
      </Text>
      <Text fz="sm" fw={700}>
        {value}
      </Text>
    </Group>
  );
}

function FetchError({ label }: { label: string }) {
  return (
    <Group
      role="status"
      gap="xs"
      align="flex-start"
      wrap="nowrap"
      m="sm"
      p="sm"
      style={{
        borderRadius: "var(--mantine-radius-md)",
        backgroundColor: "var(--mantine-color-gray-1)",
      }}
    >
      <InfoIcon
        size={16}
        aria-hidden="true"
        style={{
          color: "var(--mantine-color-gray-7)",
          flexShrink: 0,
          marginTop: 2,
        }}
      />
      <Text fz="xs" c="dark.6">
        {fetchErrorMessage(label)}
      </Text>
    </Group>
  );
}

/** PC幅：期間を行にした表 */
function PeriodTable({ periods }: { periods: Period[] }) {
  return (
    <Stack gap="xs">
      <Table withTableBorder verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>集計期間</Table.Th>
            <Table.Th ta="right">キャンセル率</Table.Th>
            <Table.Th ta="right">直前キャンセル率</Table.Th>
            <Table.Th ta="right">勤務数</Table.Th>
            <Table.Th ta="right">キャンセル件数</Table.Th>
            <Table.Th ta="right">うち直前キャンセル件数</Table.Th>
            <Table.Th ta="right">
              判定対象件数（勤務数＋キャンセル件数）
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {periods.map(({ label, description, stats }) => (
            <Table.Tr key={label}>
              <Table.Th scope="row">
                <Text fz="sm" fw={700}>
                  {label}
                </Text>
                <Text fz="xs" c="dark.5" fw={400}>
                  {description}
                </Text>
              </Table.Th>
              {stats == null ? (
                <Table.Td colSpan={6}>
                  {/* セルの役割（cell）を保つため、role はセルではなく中の要素に付ける */}
                  <div role="status">
                    <Text fz="xs" c="dark.6">
                      {fetchErrorMessage(label)}
                    </Text>
                  </div>
                </Table.Td>
              ) : (
                <>
                  <Table.Td ta="right" fz="lg" fw={700}>
                    {formatRate(stats.cancellationRate, stats.resolvedCount)}
                  </Table.Td>
                  <Table.Td ta="right" fz="lg" fw={700}>
                    {formatRate(
                      stats.lateCancellationRate,
                      stats.resolvedCount,
                    )}
                  </Table.Td>
                  <Table.Td ta="right">{stats.workedCount}回</Table.Td>
                  <Table.Td ta="right">{stats.cancellationCount}回</Table.Td>
                  <Table.Td ta="right">
                    {stats.lateCancellationCount}回
                  </Table.Td>
                  <Table.Td ta="right" fw={700}>
                    {stats.resolvedCount}件
                  </Table.Td>
                </>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {periods
        .filter(({ stats }) => stats != null && stats.resolvedCount === 0)
        .map(({ label }) => (
          <Text key={label} fz="xs" c="dark.5">
            {noRecordMessage(label)}
          </Text>
        ))}
    </Stack>
  );
}

function Explanation() {
  return (
    <Stack gap="sm" component="section" aria-label="数字の見方">
      <Text fz="sm" fw={700}>
        数字の見方
      </Text>
      <Stack gap={4}>
        <Text fz="xs" fw={700}>
          キャンセル率と直前キャンセル率の違い
        </Text>
        <Text fz="xs" c="dark.6">
          どちらも判定対象件数に対する割合です。キャンセル率はすべてのキャンセル、直前キャンセル率は勤務開始の24時間以内に行ったキャンセルだけを数えます。
        </Text>
      </Stack>
      <Stack gap={4}>
        <Text fz="xs" fw={700}>
          直近3か月と全期間の違い
        </Text>
        <Text fz="xs" c="dark.6">
          直近3か月は、直近3か月に決まったお仕事が対象で、日々入れ替わります。全期間は、これまでのすべてのお仕事が対象です。
        </Text>
      </Stack>
      <Stack gap={4}>
        <Text fz="xs" fw={700}>
          判定対象件数とは
        </Text>
        <Text fz="xs" c="dark.6">
          勤務数とキャンセル件数の合計で、応募した件数ではありません。施設の都合による一斉キャンセルなど、集計の対象外となるキャンセルは含みません。
        </Text>
      </Stack>
      <Text fz="xs" c="dark.5">
        率は小数第1位までの切り上げで表示しています。
      </Text>
    </Stack>
  );
}
